import { z } from "zod";

import { agentCoverageSchema, availabilitySearchEmptyReasonSchema } from "@/shared/agent-api/responses";
import { scopeRequestedIntentSchema } from "@/shared/agent-api/scheduling-scope";
import { intakeSmsConsentSchema } from "@/shared/agent-api/slow-thinker-intake-sms";
import { appointmentDetailsSchema, AppointmentStatus, AppointmentType } from "@/shared/models/appointment";
import { CareMode } from "@/shared/models/care-mode";
import { locationIdentitySchema } from "@/shared/models/location";
import { SelfPayFollowupStatus } from "@/shared/models/scheduling-self-pay";
import {
  CoverageMaintenance,
  coverageMaintenanceSchema,
  ReportedInsuranceUsage,
  reportedInsuranceUsageSchema,
} from "@/shared/scheduling/coverage-context";
import {
  providerSchema,
  schedulingPresentationSchema,
  schedulingResourceSchema,
  schedulingServiceSchema,
} from "@/shared/scheduling/presentation";
import { dateRangeSchema, type DateRange } from "@/shared/scheduling/search-coverage";

export { dateRangeSchema, type DateRange };
export {
  CoverageMaintenance,
  coverageMaintenanceSchema,
  pendingCoverageContextSchema,
  ReportedInsuranceUsage,
  reportedInsuranceUsageSchema,
  type PendingCoverageContext,
} from "@/shared/scheduling/coverage-context";

export { CareMode };

export {
  providerSchema,
  SchedulingPresentation,
  schedulingPresentationSchema,
  schedulingResourceSchema,
  schedulingServiceSchema,
  type SchedulingProvider,
  type SchedulingResource,
} from "@/shared/scheduling/presentation";

/**
 * Platform-owned business contract, implemented per customer for Voice, SMS and Web.
 * Read this file top to bottom: methods, context, requests, response objects and outcomes.
 * DTOs are inferred from the schemas below; there is no second handwritten wire model.
 * Working assumption pending VAL-3462: this is the internal customer-implementation
 * boundary. External adapters authorize and explicitly serialize permitted fields;
 * implementing this interface does not approve an external API or expose full entities.
 *
 * This declaration does not route traffic. Adapters must authorize context and validate both
 * input and output for HTTP AND direct calls. Schemas check shape, not ownership, consent,
 * workflow support, reference integrity or request/response correlation. Those checks belong
 * to the runtime boundary and guarded operations before any serving migration.
 */
export interface Scheduling {
  /** Patient appointments, permitted actions and evidence about the read. */
  getBookedAppointments(
    context: SchedulingContext,
    request: GetBookedAppointmentsRequest
  ): Promise<GetBookedAppointmentsResponse>;
  /** Scheduling-eligible locations, not every row in the location table. */
  getLocations(context: SchedulingContext, request: GetLocationsRequest): Promise<GetLocationsResponse>;
  /** Common assessment; customer policy chooses the evaluation strategy. */
  checkEligibility(context: SchedulingContext, request: CheckEligibilityRequest): Promise<CheckEligibilityResponse>;
  /** Action-scope screen; may precede patient identification. Not write authorization. */
  getRelevantSchedulingRules(
    context: SchedulingScopeContext,
    request: GetRelevantSchedulingRulesRequest
  ): Promise<GetRelevantSchedulingRulesResponse>;
  /** Offerable plans, whether found by deterministic search or an internal ranking agent. */
  getAvailability(context: SchedulingContext, request: GetAvailabilityRequest): Promise<GetAvailabilityResponse>;
  /** Consume a confirmed booking plan; revalidate scope, eligibility and source availability. */
  bookAppointment(context: SchedulingContext, request: BookAppointmentRequest): Promise<BookAppointmentResponse>;
  /** Execute the complete confirmed single/linked plan; never silently drop a member. */
  rescheduleAppointment(
    context: SchedulingContext,
    request: RescheduleAppointmentRequest
  ): Promise<RescheduleAppointmentResponse>;
  /** Unsupported/staff-directed cancellation returns a failure, not a successful no-op. */
  cancelAppointment(context: SchedulingContext, request: CancelAppointmentRequest): Promise<CancelAppointmentResponse>;
}

// Context: trusted invocation data, never callbacks, configuration, repositories or credentials.
// IDs/references are opaque non-empty strings. They are not authorization capabilities by themselves.
const referenceSchema = z.string().min(1);
const instantSchema = z.iso.datetime({ offset: true });
const localDateSchema = z.iso.date();
const localTimeSchema = z.iso.time({ precision: -1 });

export enum SchedulingChannel {
  Sms = "SMS",
  Voice = "VOICE",
  Web = "WEB",
}

export const channelSchema = z.enum(Object.values<`${SchedulingChannel}`>(SchedulingChannel));
export type Channel = z.infer<typeof channelSchema>;

export const schedulingScopeContextSchema = z.strictObject({
  groupId: referenceSchema,
  channel: channelSchema,
  channelSessionId: referenceSchema.optional(),
  // Resolved inbound-site/region scope. Reauthorize on a channel change.
  siteScopeRef: referenceSchema.optional(),
  requestId: referenceSchema,
});
export type SchedulingScopeContext = z.infer<typeof schedulingScopeContextSchema>;

export const schedulingContextSchema = schedulingScopeContextSchema.extend({ patientId: referenceSchema });
export type SchedulingContext = z.infer<typeof schedulingContextSchema>;

// Common vocabulary and patient-reported facts. Keep existing Valerie appointment enum values.
// INQUIRE represents the existing read/confirm permission (SchedulingAction.Confirm), not a
// confirmation mutation. Compatibility adapters translate the older lowercase action vocabulary.
export const actionSchema = z.enum(["SCHEDULE", "RESCHEDULE", "CANCEL", "INQUIRE"]);
export type Action = z.infer<typeof actionSchema>;
export const careModeSchema = z.enum(CareMode);

export enum SchedulingPatientCategory {
  Determine = "DETERMINE",
  EstablishedPatient = "ESTABLISHED_PATIENT",
  NewPatient = "NEW_PATIENT",
}

export enum SchedulingIntentKind {
  Reschedule = "reschedule",
  Schedule = "schedule",
}

export const schedulingIntentSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal(`${SchedulingIntentKind.Schedule}`),
    schedule: z.strictObject({
      patientCategory: z.enum([
        `${SchedulingPatientCategory.Determine}`,
        `${SchedulingPatientCategory.NewPatient}`,
        `${SchedulingPatientCategory.EstablishedPatient}`,
      ]),
      appointmentType: z.enum(AppointmentType).optional(),
      careMode: careModeSchema.optional(),
    }),
  }),
  z.strictObject({
    kind: z.literal(`${SchedulingIntentKind.Reschedule}`),
    // The backend discovers the authoritative unit; callers cannot supply pairing policy.
    reschedule: z.strictObject({ sourceAppointmentRef: referenceSchema }),
  }),
]);
export type SchedulingIntent = z.infer<typeof schedulingIntentSchema>;

export const patientFactsSchema = z
  .strictObject({
    appointmentReason: z.string().optional(),
    confirmedPriorVisitReasons: z.array(z.string()),
    // Caller screening answer, not verified hospital history or a policy override.
    callerReportedHospitalFollowUp: z.boolean().optional(),
    coverageMaintenance: coverageMaintenanceSchema.optional(),
    coverageVerificationPending: z.boolean().optional(),
    // Reported facts, not verified coverage or instructions to update insurance.
    // Unknown plan may be omitted or null; a payer is still required when reporting insurance.
    reportedInsurance: agentCoverageSchema.partial({ plan: true }).strict().optional(),
    reportedInsuranceUsage: reportedInsuranceUsageSchema.optional(),
    // Omitted = not asked; false and true are explicit answers.
    confirmedSelfPay: z.boolean().optional(),
    patientDeclinesTravel: z.boolean().optional(),
  })
  .superRefine(
    ({ coverageMaintenance, coverageVerificationPending, reportedInsurance, reportedInsuranceUsage }, context) => {
      if (reportedInsuranceUsage === ReportedInsuranceUsage.ReplaceStalePrimary && !reportedInsurance) {
        context.addIssue({
          code: "custom",
          path: ["reportedInsurance"],
          message: "Replacing stale primary coverage requires caller-reported insurance",
        });
      }

      if (coverageVerificationPending && (!coverageMaintenance || coverageMaintenance === CoverageMaintenance.None)) {
        context.addIssue({
          code: "custom",
          path: ["coverageMaintenance"],
          message: "Pending coverage verification requires a maintenance operation",
        });
      }

      if (
        coverageMaintenance === CoverageMaintenance.ReplacePrimary &&
        reportedInsuranceUsage !== ReportedInsuranceUsage.ReplaceStalePrimary
      ) {
        context.addIssue({
          code: "custom",
          path: ["reportedInsuranceUsage"],
          message: "Primary replacement requires replace-stale-primary usage",
        });
      }

      if (
        reportedInsuranceUsage === ReportedInsuranceUsage.ReplaceStalePrimary &&
        (coverageMaintenance !== CoverageMaintenance.ReplacePrimary || coverageVerificationPending !== true)
      ) {
        context.addIssue({
          code: "custom",
          path: ["coverageVerificationPending"],
          message: "Replacing stale primary coverage requires pending primary-replacement verification",
        });
      }
    }
  );
export type PatientFacts = z.infer<typeof patientFactsSchema>;

// Explicit public projections, not replacement core entities. Backend-only identity and
// persistence metadata stay private; distance and display address are query-time additions.
export const locationSchema = z.strictObject({
  id: locationIdentitySchema.shape.id.min(1),
  name: locationIdentitySchema.shape.name.unwrap(),
  displayAddress: z.string().optional(),
  timeZone: z.string().optional(),
  distanceMiles: z.number().nonnegative().optional(),
});
export type SchedulingLocation = z.infer<typeof locationSchema>;

// Read requests and results.
export const getBookedAppointmentsRequestSchema = z.strictObject({
  // Omitted/partial dates use bounded read defaults; results report the actual searched dates.
  dates: z
    .strictObject({ startDate: localDateSchema.optional(), endDate: localDateSchema.optional() })
    .refine(({ startDate, endDate }) => !startDate || !endDate || startDate <= endDate, {
      message: "End date precedes start date",
      path: ["endDate"],
    })
    .optional(),
  purpose: z
    .enum([
      "UPCOMING_APPOINTMENTS",
      "APPOINTMENT_INQUIRY",
      "ELIGIBILITY_HISTORY",
      "PRIOR_VISIT_HISTORY",
      "ENCOUNTER_ROUTING",
    ])
    .optional(),
  // Omitted fields retain the scheduling source's existing read defaults.
  consistency: z.enum(["PREFER_SAFE_CACHE", "PREFER_AUTHORITATIVE"]).optional(),
  // Omitted uses policy defaults; an explicitly empty filter is invalid.
  statuses: z.strictObject({ values: z.tuple([z.enum(AppointmentStatus)]).rest(z.enum(AppointmentStatus)) }).optional(),
});
export type GetBookedAppointmentsRequest = z.infer<typeof getBookedAppointmentsRequestSchema>;

export const getLocationsRequestSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("patientLocations") }),
  z.strictObject({ kind: z.literal("providerLocations"), practitionerId: referenceSchema }),
]);
export type GetLocationsRequest = z.infer<typeof getLocationsRequestSchema>;

export const locationListSchema = z.strictObject({
  locations: z.array(locationSchema),
  coverage: z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("ALL_KNOWN_LOCATIONS") }),
    z.strictObject({ kind: z.literal("BOUNDED_PERIOD"), observedDates: dateRangeSchema }),
    z.strictObject({ kind: z.literal("PATIENT_SCOPED") }),
  ]),
});
export type LocationList = z.infer<typeof locationListSchema>;

export const appointmentSchema = z.strictObject({
  appointmentRef: referenceSchema,
  valerieAppointmentId: referenceSchema.optional(),
  startsAt: instantSchema,
  durationMinutes: appointmentDetailsSchema.shape.durationMinutes.unwrap().unwrap().optional(),
  appointmentType: appointmentDetailsSchema.shape.type,
  reason: appointmentDetailsSchema.shape.ehrReason.unwrap().optional(),
  status: appointmentDetailsSchema.shape.status,
  // Resource-only and incomplete historical records need not contain a provider/location.
  ...schedulingServiceSchema.shape,
  location: locationSchema.optional(),
  permissions: z
    .strictObject({
      // Omitted means no per-appointment resource limit; empty explicitly denies actions.
      // Neither state alone authorizes a write: runtime policy must still check the request.
      allowedActions: z.array(actionSchema).optional(),
      policyAuthorizedActions: z.array(actionSchema),
      policyTags: z.array(z.string()),
    })
    .optional(),
  rescheduleUnit: z
    .strictObject({
      // Display/selection grouping; the backend re-resolves membership and policy before a move.
      memberAppointmentRefs: z
        .tuple([referenceSchema, referenceSchema])
        .refine(([first, second]) => first !== second, "Unit members must be distinct"),
      presentation: schedulingPresentationSchema.optional(),
    })
    .optional(),
});
export type SchedulingAppointment = z.infer<typeof appointmentSchema>;

export const appointmentListSchema = z.strictObject({
  appointments: z.array(appointmentSchema),
  searchedDates: z.array(dateRangeSchema),
  verification: z.strictObject({ verifiedWithEhr: z.boolean(), canVerifyWithEhr: z.boolean() }),
});
export type AppointmentList = z.infer<typeof appointmentListSchema>;

// Eligibility and scope. Missing evidence is distinct from ineligibility.
export const checkEligibilityRequestSchema = z
  .strictObject({
    intent: schedulingIntentSchema,
    facts: patientFactsSchema,
  })
  .refine(
    ({ intent, facts }) =>
      facts.callerReportedHospitalFollowUp === undefined || intent.kind === SchedulingIntentKind.Schedule,
    {
      path: ["facts", "callerReportedHospitalFollowUp"],
      message: "Hospitalization screening is valid only for booking eligibility",
    }
  )
  .refine(
    ({ intent, facts }) => facts.patientDeclinesTravel === undefined || intent.kind === SchedulingIntentKind.Schedule,
    {
      path: ["facts", "patientDeclinesTravel"],
      message: "Travel screening is valid only for booking eligibility",
    }
  );
export type CheckEligibilityRequest = z.infer<typeof checkEligibilityRequestSchema>;

export enum SchedulingPatientInput {
  AppointmentSelection = "APPOINTMENT_SELECTION",
  InsuranceMemberId = "INSURANCE_MEMBER_ID",
  InsurancePayer = "INSURANCE_PAYER",
  InsurancePlan = "INSURANCE_PLAN",
  PatientAddress = "PATIENT_ADDRESS",
  SelfPayConfirmation = "SELF_PAY_CONFIRMATION",
  TravelPreference = "TRAVEL_PREFERENCE",
  VisitReason = "VISIT_REASON",
}

export const requiredInputSchema = z.enum(Object.values<`${SchedulingPatientInput}`>(SchedulingPatientInput));
export type RequiredInput = z.infer<typeof requiredInputSchema>;

export enum EligibilityState {
  ELIGIBLE = "ELIGIBLE",
  INDETERMINATE = "INDETERMINATE",
  INELIGIBLE = "INELIGIBLE",
  NEEDS_INPUT = "NEEDS_INPUT",
}

export const eligibilityDecisionSchema = z.strictObject({
  state: z.enum(EligibilityState),
  reasons: z.array(z.string()),
  requiredInputs: z.array(requiredInputSchema),
});
export type EligibilityDecision = z.infer<typeof eligibilityDecisionSchema>;

export const eligibilityAssessmentSchema = z.strictObject({
  visitCategory: z.enum(["NEW_VISIT", "ESTABLISHED_VISIT", "HOSPITAL_FOLLOW_UP", "RESCHEDULING_VISIT"]),
  providers: z.array(
    z.strictObject({
      provider: providerSchema,
      decision: eligibilityDecisionSchema,
      preferredByReferral: z.boolean(),
      agentGuidance: z.string().optional(),
      // Display advice may say "an upcoming date"; this is not an actionable appointment time.
      upcomingAppointment: z.strictObject({ date: z.string(), providerName: z.string() }).optional(),
      nextAppointment: z
        .strictObject({ approximateDate: z.string(), appointmentType: z.enum(AppointmentType).optional() })
        .optional(),
    })
  ),
  locations: z.array(
    z.strictObject({
      location: locationSchema,
      decision: eligibilityDecisionSchema,
      agentGuidance: z.string().optional(),
    })
  ),
  visitChoices: z.array(
    z.strictObject({
      appointmentType: z.enum(AppointmentType),
      careMode: careModeSchema,
      decision: eligibilityDecisionSchema,
    })
  ),
  reason: z.string().optional(),
  diagnosisDescriptions: z.array(z.string()),
  // Resource-based workflows do not require an eligible clinician.
  decision: eligibilityDecisionSchema,
});
export type EligibilityAssessment = z.infer<typeof eligibilityAssessmentSchema>;

export const getRelevantSchedulingRulesRequestSchema = z.strictObject({
  action: actionSchema,
  appointmentReason: z.string().optional(),
  appointmentType: z.enum(AppointmentType).optional(),
  /** The clinic's booked appointment type name for an existing appointment. */
  ehrAppointmentTypeName: z.string().min(1).optional(),
  requireSpecificMedicalReason: z.boolean().optional(),
  detectWorkflowRequest: z.boolean().optional(),
});
export type GetRelevantSchedulingRulesRequest = z.infer<typeof getRelevantSchedulingRulesRequestSchema>;

export const scopeAssessmentSchema = z.strictObject({
  inScope: z.boolean(),
  prohibitingRules: z.array(z.string()),
  requestedIntent: scopeRequestedIntentSchema.optional(),
  reasoning: z.string().optional(),
  reasonIsSpecific: z.boolean().optional(),
  // Compatibility mapping: scope category "procedure" -> SURGERY_OR_PROCEDURE;
  // "other" -> OTHER_CATEGORY. This is not a new classifier.
  appointmentCategory: z.enum(["SURGERY_OR_PROCEDURE", "OTHER_CATEGORY"]).optional(),
  classificationSucceeded: z.boolean(),
});
export type ScopeAssessment = z.infer<typeof scopeAssessmentSchema>;

// Availability requests express intent/constraints, never a raw/options/agentic strategy selector.
const windowConstraints = {
  timePreferences: z.array(z.enum(["MORNING", "AFTERNOON"])),
  // Empty = unrestricted. Duplicate weekdays are invalid.
  daysOfWeek: z
    .array(z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6), z.literal(7)]))
    .refine((days) => new Set(days).size === days.length, "Weekdays must be distinct"),
  // Inclusive HH:mm bounds in the authorized clinic's local timezone.
  earliestStartTime: localTimeSchema.optional(),
  latestStartTime: localTimeSchema.optional(),
};

export const searchWindowSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("range"), range: dateRangeSchema, ...windowConstraints }),
    z.strictObject({ kind: z.literal("afterDate"), afterDate: localDateSchema, ...windowConstraints }),
    z.strictObject({ kind: z.literal("specificDate"), specificDate: localDateSchema, ...windowConstraints }),
    z.strictObject({ kind: z.literal("weekOfDate"), weekOfDate: localDateSchema, ...windowConstraints }),
  ])
  .refine(
    ({ earliestStartTime, latestStartTime }) =>
      !earliestStartTime || !latestStartTime || earliestStartTime <= latestStartTime,
    {
      message: "Latest start time precedes earliest start time",
      path: ["latestStartTime"],
    }
  );
export type SearchWindow = z.infer<typeof searchWindowSchema>;

export enum SchedulingAvailabilityRequestKind {
  Continuation = "continuation",
  Search = "search",
}

export const getAvailabilityRequestSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal(`${SchedulingAvailabilityRequestKind.Search}`),
    search: checkEligibilityRequestSchema.safeExtend({
      // The implementation resolves eligibility internally; patient facts do not authorize booking.
      // Empty uses a policy-bounded window, not an unbounded search.
      windows: z.array(searchWindowSchema),
      // Unset/empty targets are derived under policy; supplied IDs may only narrow access.
      targets: z
        .strictObject({ practitionerIds: z.array(referenceSchema), locationIds: z.array(referenceSchema) })
        .optional(),
      // Descriptive preferences, never policy overrides or hidden hard constraints.
      preferences: z.strictObject({ additionalCriteria: z.string().optional() }).optional(),
    }),
  }),
  z.strictObject({
    kind: z.literal(`${SchedulingAvailabilityRequestKind.Continuation}`),
    continuationCursor: referenceSchema,
  }),
]);
export type GetAvailabilityRequest = z.infer<typeof getAvailabilityRequestSchema>;

// Offerable plans: deterministic slots and ranked recommendations use the SAME result.
export enum SchedulingSlotKind {
  Provider = "provider",
  Resource = "resource",
}

export enum SchedulingPlanKind {
  Booking = "booking",
  Rescheduling = "rescheduling",
}

const slotFields = {
  ...schedulingServiceSchema.shape,
  // Source IDs, allocations and provenance stay behind this reference.
  slotRef: referenceSchema,
  appointmentType: z.enum(AppointmentType),
  careMode: careModeSchema,
  startsAt: instantSchema,
  endsAt: instantSchema,
  location: locationSchema,
};

export const slotSchema = z
  .discriminatedUnion("kind", [
    // kind identifies the primary scheduling target. Both identities may be known, but
    // attribution never changes the allocations bound privately to slotRef/optionRef.
    // Keep wire-literal types compatible with existing callers during incremental enum adoption.
    z.strictObject({ ...slotFields, kind: z.literal(`${SchedulingSlotKind.Provider}`), provider: providerSchema }),
    z.strictObject({
      ...slotFields,
      kind: z.literal(`${SchedulingSlotKind.Resource}`),
      resource: schedulingResourceSchema,
    }),
  ])
  .refine(({ startsAt, endsAt }) => Date.parse(endsAt) > Date.parse(startsAt), {
    message: "Slot duration must be positive",
    path: ["endsAt"],
  });
export type Slot = z.infer<typeof slotSchema>;

export const appointmentMoveSchema = z.strictObject({ sourceAppointmentRef: referenceSchema, target: slotSchema });
export type AppointmentMove = z.infer<typeof appointmentMoveSchema>;

export const bookingPlanSchema = z.strictObject({ kind: z.literal(`${SchedulingPlanKind.Booking}`), slot: slotSchema });
export type BookingPlan = z.infer<typeof bookingPlanSchema>;
export const movePlanSchema = z.strictObject({
  kind: z.literal(`${SchedulingPlanKind.Rescheduling}`),
  presentation: schedulingPresentationSchema.optional(),
  // Single or authoritative two-member unit. Pairing/order/gap policy still needs runtime validation.
  moves: z
    .union([z.tuple([appointmentMoveSchema]), z.tuple([appointmentMoveSchema, appointmentMoveSchema])])
    .refine(
      (moves) => new Set(moves.map((move) => move.sourceAppointmentRef)).size === moves.length,
      "Moves must refer to distinct appointments"
    ),
});
export type MovePlan = z.infer<typeof movePlanSchema>;

export const schedulingOptionSchema = z.strictObject({
  // Runtime must bind and revalidate the COMPLETE plan, including patient/site and source identity.
  optionRef: referenceSchema,
  // Absence does not mean forever-valid.
  expiresAt: instantSchema.optional(),
  plan: z.discriminatedUnion("kind", [bookingPlanSchema, movePlanSchema]),
  explanation: z.strictObject({
    // One-based presentation rank, when the implementation supplies it.
    rank: z.number().int().positive().optional(),
    reasons: z.array(z.string()),
    preferenceTier: z.enum(["PREFERRED", "FALLBACK", "NOT_ASSESSED"]),
  }),
});
export type SchedulingOption = z.infer<typeof schedulingOptionSchema>;

export const searchProgressSchema = z.discriminatedUnion("extent", [
  z.strictObject({ extent: z.literal("COMPLETE_FOR_REQUEST"), searchedDates: z.array(dateRangeSchema) }),
  z.strictObject({
    extent: z.literal("CONTINUABLE_PREFIX"),
    searchedDates: z.array(dateRangeSchema),
    nextCursor: referenceSchema,
  }),
  z.strictObject({ extent: z.literal("SELECTED_CANDIDATES"), searchedDates: z.array(dateRangeSchema) }),
]);
export type SearchProgress = z.infer<typeof searchProgressSchema>;

export const optionPageSchema = z.strictObject({
  // Non-empty. Runtime must also check that every plan matches the request intent.
  options: z.tuple([schedulingOptionSchema]).rest(schedulingOptionSchema),
  progress: searchProgressSchema,
  ordering: z.enum(["CUSTOMER_ORDERED", "PERMITTED_DEGRADED_ORDER"]),
});
export type OptionPage = z.infer<typeof optionPageSchema>;

export const emptySearchSchema = z.strictObject({
  // Timeout, missing input or staff escalation is NOT an empty successful search.
  reason: availabilitySearchEmptyReasonSchema,
  explanation: z.string(),
  progress: searchProgressSchema,
});
export type EmptySearch = z.infer<typeof emptySearchSchema>;

// Commands. An idempotencyKey alone does not provide deduplication or replay. The initial private
// booking binding defers those guarantees to P1; callers must not automatically retry writes.
const selectionFields = {
  optionRef: referenceSchema,
  idempotencyKey: referenceSchema,
  note: z.string().optional(),
  confirmedSelfPay: z.boolean().optional(),
};

export const bookAppointmentRequestSchema = z.strictObject({
  intakeSms: intakeSmsConsentSchema.optional(),
  ...selectionFields,
  initialBookingReasonSource: z.enum(["CALLER_REASON", "REFERRAL_REASON"]).optional(),
});
export type BookAppointmentRequest = z.infer<typeof bookAppointmentRequestSchema>;
// The initial-booking-only reason selector is deliberately not accepted on reschedule.
export const rescheduleAppointmentRequestSchema = z.strictObject(selectionFields);
export type RescheduleAppointmentRequest = z.infer<typeof rescheduleAppointmentRequestSchema>;
export const cancelAppointmentRequestSchema = z.strictObject({
  appointmentRef: referenceSchema,
  idempotencyKey: referenceSchema,
  reason: z.string().optional(),
});
export type CancelAppointmentRequest = z.infer<typeof cancelAppointmentRequestSchema>;

export const mutationSuccessSchema = z.strictObject({
  // Runtime checks cardinality against the whole executed plan, including linked moves.
  appointments: z.array(appointmentSchema).min(1),
  previousAppointmentRefs: z.array(referenceSchema),
  // Booking succeeded even when follow-up needs review; do not retry the booking.
  selfPayFollowupStatus: z.enum(["FOLLOWUP_SENT", "FOLLOWUP_NEEDS_REVIEW"]).optional(),
});
export type MutationSuccess = z.infer<typeof mutationSuccessSchema>;

/** Preserve the common wire vocabulary while sharing the writer-to-contract translation. */
export const commonSelfPayFollowupStatuses = {
  [SelfPayFollowupStatus.NEEDS_REVIEW]:
    mutationSuccessSchema.shape.selfPayFollowupStatus.unwrap().enum.FOLLOWUP_NEEDS_REVIEW,
  [SelfPayFollowupStatus.SENT]: mutationSuccessSchema.shape.selfPayFollowupStatus.unwrap().enum.FOLLOWUP_SENT,
} satisfies Record<SelfPayFollowupStatus, NonNullable<MutationSuccess["selfPayFollowupStatus"]>>;

const failureFields = { explanation: z.string().min(1), requiredInputs: z.array(requiredInputSchema) };

export enum SchedulingFailureCode {
  DeadlineExceeded = "DEADLINE_EXCEEDED",
  DependencyUnavailable = "DEPENDENCY_UNAVAILABLE",
  InvalidRequest = "INVALID_REQUEST",
  MutationStateUncertain = "MUTATION_STATE_UNCERTAIN",
  OptionNoLongerAvailable = "OPTION_NO_LONGER_AVAILABLE",
  PatientInputRequired = "PATIENT_INPUT_REQUIRED",
  StaffRequired = "STAFF_REQUIRED",
  StaleContext = "STALE_CONTEXT",
  UnsupportedWorkflow = "UNSUPPORTED_WORKFLOW",
}

export const failureSchema = z.discriminatedUnion("code", [
  z.strictObject({
    code: z.literal(`${SchedulingFailureCode.MutationStateUncertain}`),
    operationRef: referenceSchema,
    ...failureFields,
  }),
  z.strictObject({
    code: z.enum([
      `${SchedulingFailureCode.InvalidRequest}`,
      `${SchedulingFailureCode.UnsupportedWorkflow}`,
      `${SchedulingFailureCode.PatientInputRequired}`,
      `${SchedulingFailureCode.StaffRequired}`,
      `${SchedulingFailureCode.DependencyUnavailable}`,
      `${SchedulingFailureCode.DeadlineExceeded}`,
      `${SchedulingFailureCode.StaleContext}`,
      `${SchedulingFailureCode.OptionNoLongerAvailable}`,
    ]),
    operationRef: referenceSchema.optional(),
    ...failureFields,
  }),
]);
export type Failure = z.infer<typeof failureSchema>;

// Responses: explicit business outcomes. Unexpected errors still use normal error handling.
export enum SchedulingResultKind {
  Data = "data",
  Empty = "empty",
  Failure = "failure",
}

const failureResponseSchema = z.strictObject({
  kind: z.literal(`${SchedulingResultKind.Failure}`),
  failure: failureSchema,
});
const resultSchema = <T extends z.ZodType>(data: T) =>
  z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal(`${SchedulingResultKind.Data}`), data }),
    failureResponseSchema,
  ]);

export const getBookedAppointmentsResponseSchema = resultSchema(appointmentListSchema);
export type GetBookedAppointmentsResponse = z.infer<typeof getBookedAppointmentsResponseSchema>;
export const getLocationsResponseSchema = resultSchema(locationListSchema);
export type GetLocationsResponse = z.infer<typeof getLocationsResponseSchema>;
export const checkEligibilityResponseSchema = resultSchema(eligibilityAssessmentSchema);
export type CheckEligibilityResponse = z.infer<typeof checkEligibilityResponseSchema>;
export const getRelevantSchedulingRulesResponseSchema = resultSchema(scopeAssessmentSchema);
export type GetRelevantSchedulingRulesResponse = z.infer<typeof getRelevantSchedulingRulesResponseSchema>;
export const getAvailabilityResponseSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal(`${SchedulingResultKind.Data}`), data: optionPageSchema }),
  z.strictObject({ kind: z.literal(`${SchedulingResultKind.Empty}`), empty: emptySearchSchema }),
  failureResponseSchema,
]);
export type GetAvailabilityResponse = z.infer<typeof getAvailabilityResponseSchema>;
export const bookAppointmentResponseSchema = resultSchema(mutationSuccessSchema);
export type BookAppointmentResponse = z.infer<typeof bookAppointmentResponseSchema>;
export const rescheduleAppointmentResponseSchema = resultSchema(mutationSuccessSchema);
export type RescheduleAppointmentResponse = z.infer<typeof rescheduleAppointmentResponseSchema>;
export const cancelAppointmentResponseSchema = resultSchema(mutationSuccessSchema);
export type CancelAppointmentResponse = z.infer<typeof cancelAppointmentResponseSchema>;
